/**
 * sb-cart-drawer.js — SoundBird cart drawer behaviour
 * ---------------------------------------------------------------------------
 * Talks to window.SBChrome for overlay state and rides the Cart AJAX API for
 * mutations. After any change the drawer is re-rendered from the SECTION, not
 * from a JS template — the line markup exists once, in Liquid, and cannot drift.
 *
 * Vanilla, no dependencies.
 *
 * ---------------------------------------------------------------------------
 * TWO MEASUREMENT TRAPS, recorded because both cost real time on 2026-08-05 and
 * both will catch the next person debugging this file against the live store.
 *
 * 1. SHOPIFY MINIFIES THEME JS. This source is ~33 kB, heavily commented; the
 *    CDN serves ~12 kB of `(()=>{const chrome=window.SBChrome,...`. Comparing
 *    the served byte count against this file's looks exactly like the storefront
 *    running a stale build. It is not. Compare CONTENT — grep the served file
 *    for a distinctive string — never size.
 *
 * 2. THE ASSET FINGERPRINT DOES NOT ALWAYS ROLL. `{{ … | asset_url }}` kept
 *    emitting the same ?v= across several pushes while the object behind it was
 *    updating each time. A stable ?v= is not evidence of a stale file either.
 *
 * Between them these produced a confident, wrong diagnosis that the live theme
 * was serving pre-interceptor code. It never was. The file was renamed from
 * sb-cart.js on the strength of that mistake — the new name is better anyway,
 * since it matches sections/sb-cart-drawer.liquid, but the reason given at the
 * time was wrong, and an orphaned assets/sb-cart.js is left on the remote theme.
 * ---------------------------------------------------------------------------
 */
(() => {
  const chrome = window.SBChrome;
  const root = document.querySelector('[data-sb-chrome]');
  if (!chrome || !root) return;

  const SECTION = 'sb-cart-drawer';
  const CARD_STEP = 312; /* 302px card + 10px gap */
  const DRAG_SNAP = 55;
  const POOF_MS = 220;

  const cart = () => root.querySelector('[data-sb-cart]');
  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'GBP',
    }).format((Number(cents) || 0) / 100);

  /* ── Re-render ──────────────────────────────────────────────────────
     Section Rendering API. The drawer is swapped wholesale, so every derived
     state — the blocked checkout, the sold-out lines, the subtotal, the empty
     state — comes back correct without being recomputed here. */
  /**
   * Responses are applied IN ORDER, not in the order they arrive.
   *
   * Every refresh is a separate round trip, and two of them overlap whenever a
   * second line is removed before the first has come back. Nothing guaranteed
   * they resolved in order, so the earlier response — rendered when the cart
   * still held an item — could land last and paint the drawer back into the
   * filled state it had already left. Emptying the cart is exactly when this
   * fires, because that is when removals get clicked in quick succession, and
   * it is why the empty state appeared only sometimes (owner 2026-08-04: "when
   * all items are removed from cart doesn't change consistently back to the
   * default state").
   *
   * A monotonic ticket fixes it: a response is discarded if another refresh has
   * started since it was issued. Cheaper and more reliable than cancelling,
   * since the newer request is already on its way with the truth.
   */
  let refreshTicket = 0;

  /**
   * Cart WRITES run one at a time.
   *
   * The Cart AJAX API is stateful, and concurrent mutations clobber one
   * another — measured here by firing five removals 40ms apart: three landed,
   * two vanished, and the cart was left holding items whose rows had already
   * gone. That is the other half of "doesn't consistently change back to the
   * default state" (owner 2026-08-04), and the half the render guard above
   * cannot help with: no amount of re-rendering invents a removal the server
   * never performed.
   *
   * Emptying a cart is precisely when writes get fired in a burst, which is
   * why it showed up there and essentially nowhere else.
   *
   * Only the REQUESTS queue. Each row's poof still plays the moment it is
   * clicked, so the drawer stays as responsive as it was.
   */
  let writeChain = Promise.resolve();
  const enqueue = (fn) => {
    const run = writeChain.then(fn, fn);
    /* Keep the chain alive even if one write rejects. */
    writeChain = run.catch(() => {});
    return run;
  };

  async function refresh() {
    const ticket = ++refreshTicket;
    /* NO Accept header, and it matters more than it looks.
       ------------------------------------------------------------------
       This used to send `Accept: application/json`, which looks harmless and
       silently broke the drawer on every page except the homepage.

       Most storefront URLs have a JSON representation of their own, and Shopify
       honours Accept BEFORE it looks at ?sections=. So the request came back as
       the resource, not the section render:
         /products/…  -> the product JSON      {"product": …}
         /pages/…     -> the page JSON         {"page": …}
         /cart        -> the cart JSON         {"token": …}
         /collections/all -> 404
       Only "/" worked, because the index has no JSON form to serve and so fell
       through to the Section Rendering API.

       refresh() then hit `!res.ok` (collections) or found no [data-sb-cart] in
       the payload (everywhere else) and returned without touching the DOM — so
       removals left their rows, the empty state never arrived, and the count
       never moved, on every page a customer actually shops from.

       The Section Rendering API keys off ?sections= alone. Sending nothing is
       what makes it answer. Measured after the change: 200 with a real drawer on
       /, /collections/all, /products/…, /cart and /pages/…. */
    const res = await fetch(`${window.location.pathname}?sections=${SECTION}`);
    if (!res.ok || ticket !== refreshTicket) return;
    const data = await res.json();
    if (ticket !== refreshTicket) return;
    const html = data[SECTION];
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('[data-sb-cart]');
    const current = cart();
    if (!next || !current) return;

    const crossCollapsed = current.querySelector('[data-sb-cross]')?.hasAttribute('data-collapsed');
    current.replaceWith(next);

    /* Preserve the one piece of state the visitor owns rather than the cart —
       BOTH ways, not just "was collapsed". This used to only re-apply
       collapsed, and left expanded to fall out of whatever the fresh server
       markup defaulted to. That was safe while the default was unconditionally
       expanded; it silently snapped a manually-expanded cross back shut on the
       next mutation now that the default can be collapsed (mobile, or a
       closure and a reservation showing together — see the init check near
       the bottom of this file). Asserting the prior state explicitly either
       way means the server's default only ever matters once, on first paint. */
    collapseCross(next.querySelector('[data-sb-cross]'), !!crossCollapsed, false);

    chrome.setCartCount(Number(next.dataset.itemCount) || 0);
    wireLines();
    loadCrossSell();
    chrome.measureShell();
  }

  /* ── Remove ─────────────────────────────────────────────────────────
     The poof plays FIRST and the row only leaves the DOM once it has finished.
     Refreshing immediately would swap the markup out from under the animation
     and the row would just disappear. */
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  async function removeLine(key) {
    const row = root.querySelector(`[data-sb-line][data-key="${CSS.escape(key)}"]`);

    /* Fire the request FIRST and let it run under the animation. It used to
       start after the poof, so the row sat invisible but still occupying its
       78px for the whole round trip — measured at ~350ms of dead space — and
       then the whole drawer snapped to its new state at once. */
    const req = enqueue(() =>
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      })
    );

    if (row) {
      row.setAttribute('data-removing', '');
      if (!chrome.reduced()) {
        await wait(POOF_MS);
        /* Then close the space it held. The poof only fades the card — without
           this the gap stays open until the re-render replaces the markup, which
           is what read as the row hanging around after it had gone. Height has
           to be pinned to its measured value first, or there is nothing for the
           transition to run from. */
        row.style.height = `${row.offsetHeight}px`;
        void row.offsetHeight;
        row.style.transition = 'height .18s ease';
        row.style.height = '0px';
        await wait(180);
      }
      row.remove();
    }

    await req;
    await refresh();
  }

  /* ── Add (cross-sell) ─────────────────────────────────────────────────
     NO TOAST (owner 2026-08-04: "weird notification when you add a did you
     want, just don't do that notification but do a nice poof in"). The toast
     announced the add from the bottom of the drawer while the line it was
     describing appeared silently further up — two events for one action, and
     the eye followed the wrong one. The row arriving is the confirmation.

     Which row is new is read from the cart BEFORE the change: whatever key is
     present after and absent before. Keys rather than the variant id, because
     adding a variant that is already in the cart bumps its quantity instead of
     creating a line — in that case nothing is new, and the quantity ticking up
     on the existing row is the right feedback on its own. */
  async function addVariant(id) {
    const before = new Set(
      [...root.querySelectorAll('[data-sb-line]')].map((el) => el.dataset.key)
    );

    const res = await enqueue(() =>
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          /* Tells the /cart/add watcher at the foot of this file to ignore this
             one — the drawer is already open and already refreshes itself. */
          'X-SB-Internal': '1',
        },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      })
    );
    if (!res?.ok) return;
    await refresh();
    chrome.setCartCount(Number(cart()?.dataset.itemCount) || 0, { pop: true });

    const fresh = [...root.querySelectorAll('[data-sb-line]')]
      .find((el) => !before.has(el.dataset.key));
    if (fresh && !chrome.reduced()) fresh.setAttribute('data-added', '');
  }

  /* ── Line list fade ─────────────────────────────────────────────────
     Top-only, and only once actually scrolled — never at rest. */
  function wireLines() {
    const list = root.querySelector('[data-sb-lines]');
    if (!list) return;
    const sync = () => list.toggleAttribute('data-scrolled', list.scrollTop > 4);
    list.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* ── Cross-sell collapse ────────────────────────────────────────────
     Only the rail and dots collapse; the heading always stays so it can be
     reopened. Height animates between measured values. */
  /**
   * The open height, measured the way the box will actually be laid out.
   *
   * NOT scrollHeight. The rail's clip carries `margin:-8px 0 -12px` (so the
   * cards' own lift is not cut off by the horizontal overflow), and
   * scrollHeight does not account for negative margins — it reported 119px
   * where the real height is 109.24px. Animating to 119 and then settling to
   * `auto` snapped ~10px at the end of every open and started every close from
   * the wrong place: the jitter.
   *
   * Reading it back off `auto` costs one forced reflow, on a click, and is the
   * only value guaranteed to match what the browser will settle to.
   */
  function autoHeight(el) {
    const prevH = el.style.height;
    const prevT = el.style.transition;
    el.style.transition = 'none';
    el.style.height = 'auto';
    const h = el.getBoundingClientRect().height;
    el.style.height = prevH;
    void el.offsetHeight; /* flush, so restoring is not batched with the next write */
    el.style.transition = prevT;
    return h;
  }

  /**
   * A measured pixel height is needed to ANIMATE, but it must not be left on
   * the element afterwards. Held open at a fixed px value, the box keeps the
   * height it happened to have when it was measured, so any later change to its
   * contents overflows a clipped box and simply disappears. Settling back to
   * `auto` once the transition lands keeps the animation and drops the stale
   * value — and because the target is now the true auto height, that settle is
   * a no-op rather than a jump.
   */
  function collapseCross(cross, collapsed, animate = true) {
    if (!cross) return;
    const body = cross.querySelector('[data-sb-cross-body]');
    const head = cross.querySelector('[data-sb-cross-toggle]');
    if (!body) return;

    cross.toggleAttribute('data-collapsed', collapsed);
    head?.setAttribute('aria-expanded', String(!collapsed));

    if (!animate || chrome.reduced()) {
      body.style.transition = 'none';
      body.style.height = collapsed ? '0px' : 'auto';
      requestAnimationFrame(() => { body.style.transition = ''; });
      chrome.measureShell();
      return;
    }

    if (collapsed) {
      /* Start from where it is RIGHT NOW, not from a re-measurement — the box
         is already open and its rendered height is the exact truth. */
      body.style.height = `${body.getBoundingClientRect().height}px`;
      void body.offsetHeight;
      body.style.height = '0px';
    } else {
      body.style.height = `${autoHeight(body)}px`;
      body.addEventListener('transitionend', function settle(e) {
        /* Only this element's own height — descendant transitions bubble. */
        if (e.target !== body || e.propertyName !== 'height') return;
        body.style.height = 'auto';
        body.removeEventListener('transitionend', settle);
        chrome.measureShell();
      });
    }
    chrome.measureShell();
  }

  /* ── Cross-sell data ────────────────────────────────────────────────
     Recommendations for the first line in the cart. Empty carts show the model
     list instead, so there is nothing to recommend against. */
  let railIdx = 0;
  async function loadCrossSell() {
    const track = root.querySelector('[data-sb-rail-track]');
    const cross = root.querySelector('[data-sb-cross]');
    if (!track || !cross) return;

    const firstLine = root.querySelector('[data-sb-line]');
    const pid = firstLine?.dataset.productId;
    if (!pid) { cross.hidden = true; return; }

    /* Skeletons go up BEFORE the request, not after it resolves. The block is
       the same height either way, so when the real cards arrive they replace a
       shape that is already there instead of appearing into an empty space. */
    if (!track.children.length) {
      track.innerHTML = Array.from({ length: 3 }, () =>
        '<div class="sb-c-xcard" data-skeleton aria-hidden="true">' +
        '<span class="sb-c-skel sb-c-skel--thumb"></span>' +
        '<div class="sb-c-xcard__main">' +
        '<span class="sb-c-skel sb-c-skel--line sb-c-skel--name"></span>' +
        '<span class="sb-c-skel sb-c-skel--line sb-c-skel--cat"></span>' +
        '<span class="sb-c-skel sb-c-skel--line sb-c-skel--price"></span>' +
        '</div><span class="sb-c-skel sb-c-skel--add"></span></div>').join('');
      cross.hidden = false;
      /* Place the rail NOW, at the same lead-in the real cards will use. The
         skeletons otherwise sat flush against the left edge and the whole rail
         shifted right the moment the content arrived — which is the jump the
         skeletons exist to prevent, just moved along the other axis. */
      railIdx = 0;
      positionRail(false);
      chrome.measureShell();
    }

    /* `complementary` is the better suggestion when the merchant has curated it
       in Search & Discovery, but it returns an EMPTY list when they have not —
       which is the case here today, and an empty list is indistinguishable from
       a failure at the call site. Fall back to `related` so the rail is never
       silently missing; if complementary is configured later it simply wins. */
    const fetchRecs = async (intent) => {
      const res = await fetch(`/recommendations/products.json?product_id=${pid}&limit=6&intent=${intent}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.products || []).filter((p) => p.available);
    };

    try {
      let items = await fetchRecs('complementary');
      if (!items.length) items = await fetchRecs('related');
      if (!items.length) { track.innerHTML = ''; cross.hidden = true; chrome.measureShell(); return; }
      cross.hidden = false;

      track.innerHTML = items
        .map((p) => {
          const v = p.variants.find((x) => x.available) || p.variants[0];
          const img = p.featured_image || p.images?.[0];
          return (
            '<div class="sb-c-xcard">' +
            (img ? `<img class="sb-c-xcard__thumb" src="${esc(img)}" alt="" width="52" height="52" loading="lazy">` : '<span class="sb-c-xcard__thumb"></span>') +
            '<div class="sb-c-xcard__main">' +
            /* A link, not a div. The name turns accent on hover, and a colour
               change that leads nowhere is a promise the card cannot keep. */
            `<a class="sb-c-xcard__name" href="${esc(p.url)}">${esc(p.title)}</a>` +
            `<div class="sb-c-xcard__cat">${esc(p.type || p.vendor || '')}</div>` +
            `<div class="sb-c-xcard__price">${money(v.price)}</div>` +
            '</div>' +
            `<button class="sb-c-xcard__add" type="button" data-sb-add="${v.id}" data-title="${esc(p.title)}" aria-label="Add ${esc(p.title)}">` +
            '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5v14"/></svg>' +
            '<span>Add</span></button></div>'
          );
        })
        .join('');

      renderDots(items.length);
      railIdx = 0;
      positionRail(false);
      collapseCross(cross, cross.hasAttribute('data-collapsed'), false);
    } catch {
      track.innerHTML = '';
      cross.hidden = true;
    }
    chrome.measureShell();
  }

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function renderDots(n) {
    const dots = root.querySelector('[data-sb-dots]');
    if (!dots) return;
    dots.innerHTML = Array.from({ length: n }, (_, i) =>
      `<button class="sb-c-dot" type="button" data-sb-dot="${i}" aria-label="Go to suggestion ${i + 1}"></button>`).join('');
    syncDots();
  }

  function syncDots() {
    root.querySelectorAll('[data-sb-dot]').forEach((d) =>
      d.toggleAttribute('data-on', Number(d.dataset.sbDot) === railIdx));
  }

  /* A constant lead-in keeps the active card CENTRED in the drawer with its
     neighbours peeking, rather than flush against the left gutter. */
  function leadIn() {
    const rail = root.querySelector('[data-sb-rail]');
    if (!rail) return 0;
    return Math.max(0, (rail.clientWidth - 302) / 2);
  }

  function positionRail(animate = true) {
    const track = root.querySelector('[data-sb-rail-track]');
    const clip = root.querySelector('[data-sb-rail-clip]');
    if (!track || !clip) return;
    /* NOTHING TO POSITION AGAINST WHILE THE DRAWER IS SHUT (2026-08-19).
       The drawer is display:none when closed now, because Safari 26 samples
       fixed boxes at the viewport edges even at opacity:0 and was tinting the
       chin cream from this panel (see snippets/sb-chrome-styles.liquid). Under
       display:none nothing inside has a width, so leadIn() reads 0 and centres
       the cards against nothing.

       This path is reached with the drawer shut on every add made from a
       product page: cart:update runs refresh() — which ends in loadCrossSell()
       — BEFORE it opens the drawer. Bailing out is safe rather than merely
       harmless, because the sb:overlay handler below calls positionRail again
       on open, which is the first moment the measurement means anything. */
    if (!clip.clientWidth) return;
    /* The lead-in is what centres the active card, and it has its own .42s
       transition for paging. On the FIRST placement there is nothing to
       animate from — leaving it on made the rail slide in from the left edge
       every time the drawer opened. */
    clip.style.transition = animate ? '' : 'none';
    clip.style.paddingLeft = `${leadIn()}px`;
    track.style.transition = animate ? `transform .42s var(--sb-ch-ease)` : 'none';
    track.style.transform = `translateX(${-railIdx * CARD_STEP}px)`;
    if (!animate) {
      /* Restore the transition once the value has been applied, or paging
         afterwards would jump instead of slide. */
      void clip.offsetWidth;
      requestAnimationFrame(() => { clip.style.transition = ''; });
    }
    syncDots();
    syncRailFades();
  }

  function syncRailFades() {
    const n = root.querySelectorAll('.sb-c-xcard').length;
    const fl = root.querySelector('[data-sb-rail-fade-l]');
    const fr = root.querySelector('[data-sb-rail-fade-r]');
    /* Left only once paged past the start, right only while cards remain. */
    fl?.toggleAttribute('data-on', railIdx > 0);
    fr?.toggleAttribute('data-on', railIdx < n - 1);
  }

  /* ── Rail drag ──────────────────────────────────────────────────────
     Pointer events with capture, following the finger live. Release past 55px
     pages; anything less springs back to where it was. */
  let dragging = false;
  let startX = 0;
  let dx = 0;

  root.addEventListener('pointerdown', (e) => {
    const track = e.target.closest('[data-sb-rail-track]');
    if (!track || e.target.closest('[data-sb-add]')) return;
    dragging = true;
    dx = 0;
    startX = e.clientX;
    track.setAttribute('data-dragging', '');
    track.style.transition = 'none';
    track.setPointerCapture?.(e.pointerId);
  });

  root.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const track = root.querySelector('[data-sb-rail-track]');
    if (!track) return;
    dx = e.clientX - startX;
    track.style.transform = `translateX(${-railIdx * CARD_STEP + dx}px)`;
  });

  const endDrag = () => {
    if (!dragging) return;
    dragging = false;
    const track = root.querySelector('[data-sb-rail-track]');
    const n = root.querySelectorAll('.sb-c-xcard').length;
    track?.removeAttribute('data-dragging');
    if (dx <= -DRAG_SNAP) railIdx = Math.min(n - 1, railIdx + 1);
    else if (dx >= DRAG_SNAP) railIdx = Math.max(0, railIdx - 1);
    positionRail(true);
  };
  root.addEventListener('pointerup', endDrag);
  root.addEventListener('pointercancel', endDrag);

  /* ── Delegated clicks ───────────────────────────────────────────────── */
  root.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-sb-remove]');
    if (rm) { e.preventDefault(); removeLine(rm.dataset.sbRemove); return; }

    const add = e.target.closest('[data-sb-add]');
    if (add) { e.preventDefault(); addVariant(add.dataset.sbAdd); return; }

    const toggle = e.target.closest('[data-sb-cross-toggle]');
    if (toggle) {
      const cross = toggle.closest('[data-sb-cross]');
      collapseCross(cross, !cross.hasAttribute('data-collapsed'));
      return;
    }

    const dot = e.target.closest('[data-sb-dot]');
    if (dot) { railIdx = Number(dot.dataset.sbDot); positionRail(true); }
  });

  /* Opening the cart is when the recommendations are worth fetching — doing it
     on page load would cost every visitor a request they may never see. */
  root.addEventListener('sb:overlay', (e) => {
    if (e.detail.overlay !== 'cart') return;
    wireLines();
    /* Re-fetch whenever the rail is empty, rather than latching a "loaded" flag
       on the first open. The flag was set even when the open happened with an
       EMPTY cart — where there is no rail to fill at all — so a cart that
       gained items without a cart:update reaching this tab would then open to a
       permanently blank block. Keying off the rail's actual contents cannot get
       out of step with them. */
    const track = root.querySelector('[data-sb-rail-track]');
    if (track && !track.querySelector('.sb-c-xcard:not([data-skeleton])')) loadCrossSell();
    else positionRail(false);
  });

  addEventListener('resize', () => positionRail(false), { passive: true });

  /* Adds made anywhere else on the page (product form, quick-add) refresh the
     drawer and open it, which is what the previous drawer did on add — the
     handoff does not cover that path, so the store's existing behaviour stands.
     An add that came FROM the drawer is already open and is left alone. */
  document.addEventListener('cart:update', async (e) => {
    await refresh();
    const action = e.detail?.action ?? e.detail?.resource?.action;
    if (chrome.overlay !== 'cart' && action !== 'remove') chrome.open('cart');
  });

  /* ── Adds made by code that is not this theme ────────────────────────────
     Owner 2026-08-05: "for EComposer when I add something to cart, I don't want
     a separate cart page — it should just be done through this new cart system."

     The handler above only fires on `cart:update`, which is Horizon's own event.
     EComposer builds the homepage and the collection landing pages, and its
     add-to-cart never dispatches it, so on exactly the pages that matter most
     the drawer stayed shut.

     Rather than special-casing EComposer, this watches the Cart AJAX API itself.
     Anything that successfully POSTs to /cart/add — EComposer, a Shopify app, a
     buy button, a section this theme has not met yet — announces a cart:update
     and funnels into the single handler above. One path, no per-integration
     code, and nothing to update when an app changes its markup.

     Both transports are wrapped because apps split between them: modern code
     uses fetch, older code and anything jQuery-based goes through XHR.

     The 120ms deferral is the de-duplicator. Theme code posts to /cart/add AND
     dispatches cart:update, and the order of the two is not guaranteed, so
     announcing immediately would double-refresh every theme add. Waiting a beat
     and checking whether anyone else has already announced means the watcher
     only speaks when nothing else did.

     Requests carrying X-SB-Internal are the drawer's own cross-sell adds, which
     already refresh and are already open. */
  {
    const ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
    let lastAnnounce = 0;

    document.addEventListener('cart:update', () => { lastAnnounce = performance.now(); });

    const announce = () => {
      const at = performance.now();
      setTimeout(() => {
        if (lastAnnounce >= at) return; /* somebody else already did */
        document.dispatchEvent(
          new CustomEvent('cart:update', { detail: { action: 'add', source: 'sb-watch' } })
        );
      }, 120);
    };

    const isInternal = (init) => {
      const h = init?.headers;
      if (!h) return false;
      if (typeof h.get === 'function') return !!h.get('X-SB-Internal');
      return Object.keys(h).some((k) => k.toLowerCase() === 'x-sb-internal');
    };

    /* ── Native /cart/add form posts ──────────────────────────────────────
       Owner 2026-08-05: "whenever I add something to cart it still kicks me
       into the actual cart page."

       Not an EComposer preference — measured EComposer.configs.ajax_cart is
       {enable:false}, so its add-to-cart is a real
       <form action="/cart/add" method="post"> with a name="add" submit. A form
       post is a browser navigation: Shopify accepts it and redirects to /cart.
       No "after added to cart" dropdown can prevent that, because nothing
       JavaScript-side is involved at all.

       So the form is submitted over fetch instead and the navigation never
       happens. The wrapper below sees that fetch, announces cart:update, and
       the drawer opens exactly as it does everywhere else — one path for every
       add on the site.

       WINDOW, capture phase — not document. Capture runs window -> document ->
       ... -> target, so a listener on window sees the event before one on
       document. EComposer binds at document level, and on the per-brand
       collection template it registers before this file loads, which meant a
       document-capture listener here could not stop it: the add went through
       AND ecom.js still navigated to /cart a moment later. Traced exactly that
       on /collections/apple — one fetch to /cart/add from here, then a
       beforeunload. Moving up to window puts this first on every template.

       Buy-now / checkout submits are let through untouched: those are meant to
       leave the page.

       If the add FAILS (sold out, variant gone) the form is submitted natively
       after all, so the customer still gets Shopify's own error rather than a
       click that silently does nothing. */
    window.addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!ADD_RE.test(form.getAttribute('action') || '')) return;

        const submitter = e.submitter;
        const intent = `${submitter?.name || ''} ${submitter?.value || ''} ${submitter?.className || ''}`;
        if (/checkout|buy.?now|payment/i.test(intent)) return;

        e.preventDefault();

        const body = new FormData(form);
        if (submitter?.name) body.append(submitter.name, submitter.value);

        window
          .fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body })
          .then((r) => {
            if (r.ok) return; /* the fetch wrapper announces it from here */
            form.submit(); /* real failure — let Shopify say why */
          })
          .catch(() => form.submit());
      },
      true
    );

    /* ── EComposer's JS add-to-cart buttons ───────────────────────────────
       The form interceptor above only catches real <form action="/cart/add">
       posts, which is what the EComposer PRODUCT page uses. Its collection
       cards are different: type="button", no form action, and ecom.js handles
       the click itself.

       Traced what that handler does — no form.submit(), no location.assign or
       .replace, just an assignment to window.location from
       ecomposer-136/assets/ecom.js. Unstoppable once it runs, and untrappable
       from here, because window.location's href setter cannot be redefined.

       It only behaves that way because EComposer.configs.ajax_cart is
       {enable:false} on this store — with its AJAX cart off, redirecting to
       /cart is its fallback, and no "after added to cart" setting overrides
       that. The owner had already set this button to "Show a message"
       (data-action="message") and it still redirected.

       So the click is taken in the CAPTURE phase, before ecom.js's own
       delegated handler ever sees it, and the add is done here instead. The
       fetch wrapper below then announces it and the drawer opens.

       WINDOW, capture phase, for the reason given on the submit handler above:
       document-capture lost the race to ecom.js on the per-brand collection
       template, and the page redirected to /cart after the add had already
       landed. Verified on /collections/apple, which was the template that still
       redirected after the first fix.

       Scoped to EComposer's own add-to-cart classes so nothing else on their
       cards — variant swatches, quick view, the card link — is affected. If
       EComposer ever renames these, adds start redirecting again rather than
       silently failing, which is the safer way round.

       Trade-off worth knowing: EComposer's own button feedback ("Added to
       Cart") no longer fires, since its handler is skipped. The drawer opening
       is the confirmation instead. */
    window.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest?.(
          '.ecom-ajax-cart-submit, [data-child-name="add_to_cart_button"]'
        );
        if (!btn || btn.disabled) return;

        const form = btn.closest('form');
        if (!form) return;

        const data = new FormData(form);
        const id = data.get('variant_id') || data.get('id');
        if (!id) return; /* nothing to add — let their handler deal with it */

        e.preventDefault();
        e.stopImmediatePropagation();

        const quantity = Number(data.get('quantity')) || 1;
        window.fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
        });
      },
      true
    );

    const nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (...args) {
        const out = nativeFetch.apply(this, args);
        try {
          const input = args[0];
          const url = typeof input === 'string' ? input : input?.url || '';
          if (ADD_RE.test(url) && !isInternal(args[1]) && !isInternal(input)) {
            out.then((r) => { if (r?.ok) announce(); }).catch(() => {});
          }
        } catch (err) { /* never let the watcher break somebody else's add */ }
        return out;
      };
    }

    const XHR = window.XMLHttpRequest?.prototype;
    if (XHR?.open && XHR?.send) {
      const nativeOpen = XHR.open;
      const nativeSend = XHR.send;
      XHR.open = function (method, url, ...rest) {
        try { this.__sbCartAdd = ADD_RE.test(String(url)); } catch (err) { /* noop */ }
        return nativeOpen.call(this, method, url, ...rest);
      };
      XHR.send = function (...args) {
        try {
          if (this.__sbCartAdd) {
            this.addEventListener('load', () => {
              if (this.status >= 200 && this.status < 300) announce();
            });
          }
        } catch (err) { /* noop */ }
        return nativeSend.apply(this, args);
      };
    }
  }

  /* ── Cross-sell default state ─────────────────────────────────────
     Owner, 2026-08-19: collapsed by default on mobile, and collapsed by
     default whenever a closure and a reservation are both showing (any
     width) — vertical space is already tight there before "Did you want?"
     adds another block under it.

     Runs ONCE, synchronously, on first paint — before any click could have
     reached the toggle — so it can never fight a real choice. It reads
     .sb-away__text--split rather than duplicating the away-notice's own
     date/reservation logic here: that class only exists in the DOM when
     BOTH conditions are already true (snippets/sb-away-notice.liquid), so
     this is one query against a fact already computed, not a second copy of
     the rule that could drift from it.

     Whatever this decides becomes the "prior state" refresh() now preserves
     in both directions on every later cart mutation — see the note there. */
  const crossInit = root.querySelector('[data-sb-cross]');
  if (crossInit && !crossInit.hasAttribute('data-collapsed')) {
    const bothActive = !!root.querySelector('.sb-away__text--split');
    if (chrome.isMobile() || bothActive) collapseCross(crossInit, true, false);
  }

  wireLines();
})();
